/* ==========================================================================
   ZARQA COUTURE - motion
   --------------------------------------------------------------------------
   Loaded after shop.js, so the cards already exist in the DOM.

   Rules this file follows (each one learned the hard way on this site):
     1. Nothing above the fold is ever hidden waiting to animate.
     2. If GSAP fails to load, the site is simply still - never blank.
     3. Transform and opacity only.
     4. Scrolling is the browser's own. No smooth-scroll library: on a Mac
        trackpad it makes the page feel delayed however fast it renders.
     5. Nothing is created inside a scroll handler. The marquee writes two
        numbers per frame through quickSetter on a single ticker.
     6. Boot with setTimeout, not requestAnimationFrame - rAF never fires in
        a background tab, so a page opened behind another would never animate.
   ========================================================================== */

(function () {
  "use strict";

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced || typeof window.gsap === "undefined" || typeof window.ScrollTrigger === "undefined") return;

  gsap.registerPlugin(ScrollTrigger);

  const EASE = "power3.out";
  const CLOTH = "expo.out";
  const belowFold = (el) => el.getBoundingClientRect().top > window.innerHeight * 0.92;

  /* ---------- gold thread across the top ---------- */
  function initProgress() {
    const bar = document.querySelector("#progress");
    if (!bar) return;
    gsap.to(bar, { scaleX: 1, ease: "none", scrollTrigger: { start: 0, end: "max", scrub: 0.3 } });
  }

  /* ---------- home hero: one orchestrated entrance ---------- */
  function initHero() {
    const hero = document.querySelector(".hero");
    if (!hero) return;
    const mark = hero.querySelector(".hero__mark .z");
    const tl = gsap.timeline({ defaults: { ease: CLOTH } });

    if (mark) {
      const text = mark.textContent.trim();
      mark.setAttribute("aria-label", text);
      mark.textContent = "";
      const letters = text.split("").map((ch) => {
        const s = document.createElement("span");
        s.textContent = ch;
        s.style.display = "inline-block";
        s.setAttribute("aria-hidden", "true");
        mark.appendChild(s);
        return s;
      });
      tl.from(letters, { yPercent: 110, opacity: 0, duration: 1.1, stagger: 0.05 }, 0);
    }
    const seq = [".hero__mark .c", ".hero__mark .sub", ".hero__thesis", ".hero__note", ".hero__cta", ".hero__trust"];
    seq.forEach((sel, i) => {
      const el = hero.querySelector(sel);
      if (el) tl.from(el, { opacity: 0, y: 20, duration: 0.9, ease: EASE }, 0.3 + i * 0.08);
    });

    const main = hero.querySelector(".hero__main");
    if (main) {
      tl.from(main, { clipPath: "inset(0% 0% 100% 0%)", duration: 1.4, ease: "power4.inOut" }, 0.1);
      tl.from(main.querySelector("img"), { scale: 1.14, duration: 1.9, ease: EASE }, 0.1);
    }
    const inset = hero.querySelector(".hero__inset");
    if (inset && getComputedStyle(inset).display !== "none") {
      tl.from(inset, { opacity: 0, y: 40, duration: 1.1, ease: EASE }, 0.8);
    }
    const tag = hero.querySelector(".hero__tag");
    if (tag) tl.from(tag, { opacity: 0, y: 14, duration: 0.8, ease: EASE }, 1.1);

    /* the main photograph drifts as you scroll off it */
    const img = main && main.querySelector("img");
    if (img) {
      gsap.to(img, {
        yPercent: 5, ease: "none",
        scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: true },
      });
    }
  }

  /* ---------- section headings ---------- */
  function initHeadings() {
    document.querySelectorAll(".section-head").forEach((head) => {
      if (!belowFold(head)) return;
      const parts = head.querySelectorAll(".label, h2, p, .link-arrow");
      gsap.from(parts, {
        opacity: 0, y: 22, duration: 0.95, stagger: 0.08, ease: EASE,
        scrollTrigger: { trigger: head, start: "top 88%", toggleActions: "play none none none" },
      });
    });
  }

  /* ---------- cards and panels, revealed in batches as they arrive ---------- */
  /* A collection row arrives as one piece: its cards sit in a sideways
     scroller, which would clip them while they rose into place. */
  const REVEAL = ".card, .craft__card, .way, .steps li, .spread__media, .spread__body, .band, .detail-band, .shelf, .trousseau, .about__word, .about__together, .about__portrait, .about__founder-body";
  const inShelf = (el) => !el.classList.contains("shelf") && el.closest(".shelf");
  function initReveals() {
    const items = gsap.utils.toArray(REVEAL).filter((el) => !inShelf(el)).filter(belowFold);
    if (!items.length) return;
    gsap.set(items, { opacity: 0, y: 34 });
    ScrollTrigger.batch(items, {
      start: "top 90%",
      once: false,
      onEnter: (batch) => gsap.to(batch, {
        opacity: 1, y: 0, duration: 1, stagger: 0.08, ease: CLOTH, overwrite: true,
      }),
    });
  }

  /* ---------- the hand-work marquee ---------- */
  function initMarquee() {
    const track = document.querySelector("#marquee-track");
    const group = track && track.querySelector(".marquee__group");
    if (!group) return;

    const needed = Math.max(2, Math.ceil((window.innerWidth * 2) / group.offsetWidth) + 1);
    for (let i = 1; i < needed; i++) track.appendChild(group.cloneNode(true));

    const width = group.offsetWidth;
    const words = gsap.utils.toArray(".marquee__word", track);
    const setX = gsap.quickSetter(track, "x", "px");
    const setSkew = gsap.quickSetter(words, "skewX", "deg");

    const BASE = 40;
    let x = 0, speed = 1, targetSpeed = 1, skew = 0, targetSkew = 0;

    /* only runs while the marquee is on screen, so it costs nothing elsewhere on the page */
    let visible = true;
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { rootMargin: "120px 0px" }).observe(track);
    }

    gsap.ticker.add((time, deltaMS) => {
      if (!visible) return;
      const dt = Math.min(deltaMS, 50) / 1000;
      speed += (targetSpeed - speed) * 0.08;
      skew += (targetSkew - skew) * 0.12;
      targetSpeed += ((targetSpeed < 0 ? -1 : 1) - targetSpeed) * 0.04;
      targetSkew += (0 - targetSkew) * 0.06;
      x -= BASE * speed * dt;
      if (x <= -width) x += width; else if (x > 0) x -= width;
      setX(x);
      setSkew(skew);
    });

    ScrollTrigger.create({
      start: 0, end: "max",
      onUpdate: (self) => {
        const v = self.getVelocity();
        targetSpeed = (v < 0 ? -1 : 1) * gsap.utils.clamp(1, 6, 1 + Math.abs(v) / 420);
        targetSkew = gsap.utils.clamp(-10, 10, -v / 300);
      },
    });
  }

  /* ---------- shop filters re-render the grid: a quick settle, nothing more ---------- */
  function initRerender() {
    document.addEventListener("zarqa:rendered", () => {
      const cards = document.querySelectorAll("#shop-grid .card");
      if (!cards.length) return;
      gsap.fromTo(cards, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.6, stagger: 0.035, ease: EASE, overwrite: true });
      ScrollTrigger.refresh();
    });
  }

  /* ---------- safety net: anything the reader has reached must be visible ---------- */
  function initRescue() {
    const rescue = () => {
      document.querySelectorAll(REVEAL + ", .section-head > *").forEach((el) => {
        if (parseFloat(getComputedStyle(el).opacity) >= 0.05) return;
        if (el.getBoundingClientRect().top < window.innerHeight * 0.96) gsap.to(el, { opacity: 1, y: 0, duration: 0.4 });
      });
    };
    window.addEventListener("scroll", () => { clearTimeout(rescue.t); rescue.t = setTimeout(rescue, 350); }, { passive: true });
    setTimeout(rescue, 3500);
  }

  let booted = false;
  function start() {
    if (booted) return;
    booted = true;
    initProgress();
    initHero();
    initHeadings();
    initReveals();
    initMarquee();
    initRerender();
    initRescue();
    window.addEventListener("load", () => ScrollTrigger.refresh());
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => setTimeout(start, 0));
  else setTimeout(start, 0);
  window.addEventListener("load", () => setTimeout(start, 0));
})();
